import { useActiveMember } from 'hooks';
import { useFetchActiveMemberCount } from 'hooks/models/member/useActiveMemberCount';
import { DateTime } from 'luxon';
import { fullName } from 'utils/memberUtils';
import { REFERRAL_CHANNEL_URL } from '../constants/constants';
import useSlackClient from './useSlackClient';

export interface IReferralSubmission {
  name: string;
  phone: string;
  email?: string;
  company?: string;
  notes?: string;
}

// Slack rejects a section whose text is longer than this.
const SLACK_SECTION_LIMIT = 3000;

// Slack reads &, < and > in mrkdwn as markup, so `<!channel>` or `<url|label>` in a submitted value would
// ping the channel or render as a disguised link. Escaping them makes Slack show the value as typed.
export function escapeSlackText(text: string) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Escaping can make text up to five times longer, so cut the value (never mid-entity) to fit the space left.
function escapeSlackTextWithin(text: string, maxLength: number) {
  const escaped = escapeSlackText(text);
  if (escaped.length <= maxLength) {
    return escaped;
  }

  let fitted = '';
  for (const char of text) {
    const next = escapeSlackText(char);
    if (fitted.length + next.length > maxLength - 1) {
      break;
    }
    fitted += next;
  }
  return `${fitted}…`;
}

export default function useSlackReferral() {
  const client = useSlackClient();
  const member = useActiveMember();
  const fetchActiveMemberCount = useFetchActiveMemberCount();

  async function getCompanySize() {
    try {
      return (await fetchActiveMemberCount()).toLocaleString();
    } catch {
      return 'N/A';
    }
  }

  async function submitReferral(referral: IReferralSubmission) {
    const friendDetails = [
      `*Referred Friend's Name*: ${escapeSlackText(referral.name)}`,
      `*Referred Friend's Phone*: ${escapeSlackText(referral.phone)}`,
      `*Referred Friend's Email*: ${escapeSlackText(referral.email || '')}`,
      `*Referred Company Name*: ${escapeSlackText(referral.company || '')}`,
    ].join('\n');
    const notesLabel = '\n*Anything We Should Know*: ';
    const notes = escapeSlackTextWithin(
      referral.notes || '',
      SLACK_SECTION_LIMIT - friendDetails.length - notesLabel.length
    );
    const friend = `${friendDetails}${notesLabel}${notes}`;

    const referrer = [
      `*Name*: ${escapeSlackText(fullName(member))}`,
      `*Email*: ${escapeSlackText(member.email ?? '')}`,
      `*Phone*: ${escapeSlackText(member.phone ?? '')}`,
      `*Member ID*: ${member.id}`,
      `*Organization ID*: ${member.organizationId}`,
      `*Organization Name*: ${escapeSlackText(member.organization?.organizationName ?? '')}`,
      `*Company Size*: ${await getCompanySize()}`,
      `*Position ID*: ${member.positionId}`,
      `*Position Name*: ${escapeSlackText(member.position?.title ?? '')}`,
      `*Date*: ${DateTime.local().toFormat('MMM dd, yyyy')}`,
    ].join('\n');

    const requestBody = {
      text: `Referral: ${escapeSlackText(referral.name)}`,
      blocks: [
        { type: 'section', text: { type: 'mrkdwn', text: friend } },
        { type: 'divider' },
        { type: 'section', text: { type: 'mrkdwn', text: referrer } },
      ],
    };

    return client.post(REFERRAL_CHANNEL_URL, JSON.stringify(requestBody));
  }

  return submitReferral;
}
